import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from 'react';
import PropTypes from 'prop-types';

// Import da Material-UI v4
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TextField,
    Typography
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import {getDynamicFormConfig} from '../../../../services/DynamicFormService';
import {dynamicFormStyle, MenuProps} from "../../../../styles/components/DynamicComponentStyle";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import {BaseStyle} from "../../../../styles/shared/BaseStyle";

const fieldConfig = {
    fiscal_code: {
        placeholder: "RSSMRA85M01H501Z",
        maxLength: 16,
        pattern: "^[A-Za-z]{6}[0-9]{2}[A-Za-z][0-9]{2}[A-Za-z][0-9]{3}[A-Za-z]$",
        errorMessage: "Il codice fiscale non è valido. Controlla lunghezza e formato."
    },
    cellular: {
        placeholder: "+39 333 1234567",
        maxLength: 15,
        pattern: "^(\\((00|\\+)39\\)|(00|\\+)39)?\\d{10}$",
        errorMessage: "Numero di cellulare non valido. Usa solo numeri e spazi, max 15 caratteri."
    },
    email: {
        placeholder: "esempio@email.com",
        type: "email",
        pattern: "^((?!\\.)[\\w\\-_.]*[^.])(@\\w+)(\\.\\w+(\\.\\w+)?[^.\\W])$",
        errorMessage: "Formato email non valido. Controlla che sia del tipo esempio@email.com."
    },
    vat_number: {
        placeholder: "IT01234567890",
        maxLength: 13,
        pattern: "^(IT)?[0-9]{11}$",
        errorMessage: "La Partita IVA deve iniziare con 'IT' (opzionale) seguito da 11 cifre."
    },
    money: {
        placeholder: "1234.56 o 1,234.56",
        pattern: "^-?[0-9]+(,[0-9]{3})*(\\.[0-9]{1,2})?$",
        errorMessage: "L'importo deve essere un numero valido con massimo due cifre decimali separate da un punto. La virgola per le migliaia è opzionale."
    },
    date: {
        pattern: "^(0[1-9]|[12][0-9]|3[01])\\/(0[1-9]|1[0-2])\\/\\d{4}$",
        errorMessage: "Formato non valido (DD/MM/YYYY)"
    }
};

const DynamicFormExt = forwardRef(({
                                       dynamicFormStructureId,
                                       fields,
                                       clienteCensito,
                                       onDataChange,
                                       categoria = null,
                                       initialValidation = false
                                   }, ref) => {
    const classes = dynamicFormStyle();
    const classesBase = BaseStyle();

    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [dynamicForm, setDynamicForm] = useState([]);

    useImperativeHandle(ref, () => ({
        validateForm
    }));

    const validateForm = useCallback(() => {
        const errors = {};

        dynamicForm.forEach(field => {
            const value = formValues[field.name] ?? '';
            let errorMessage = null; // Se non ci sono errori, sarà null

            // Controllo se il campo è obbligatorio
            if ((field.required ? (field.required === "true") : false) && !value) {
                errorMessage = "Questo campo è obbligatorio";
            }
            // Validazione basata sul tipo di campo
            else if (field.type && fieldConfig[field.type]) {
                const {pattern, errorMessage: typeError} = fieldConfig[field.type];
                if (!new RegExp(pattern).test(value)) {
                    errorMessage = typeError;
                }
            }

            // Imposta l'errore se presente, altrimenti null
            errors[field.name] = errorMessage;
        });

        // ------------------ VALIDAZIONE PER LA TABELLA ------------------
        dynamicForm.forEach(field => {
            if (field.type === "table" && Array.isArray(formValues[field.name])) {
                const tableErrors = formValues[field.name].map((row, rowIndex) => {
                    const rowErrors = {};
                    field.options.forEach(option => {
                        const cellValue = row[option.value] ?? '';
                        let cellErrorMessage = null;

                        // Controllo se la cella è obbligatoria
                        if (field.required && !cellValue) {
                            cellErrorMessage = "Questo campo è obbligatorio";
                        }
                        // // Validazione basata sul tipo di campo della colonna
                        // else if (option.type && fieldConfig[option.type]) {
                        //     const { pattern, errorMessage: typeError } = fieldConfig[option.type];
                        //     if (!new RegExp(pattern).test(cellValue)) {
                        //         cellErrorMessage = typeError;
                        //     }
                        // }

                        if (cellErrorMessage) {
                            rowErrors[option.value] = cellErrorMessage;
                        }

                        // Aggiorna gli errori della tabella

                        errors[`${field.name}-${rowIndex}-${option.value}`] = cellErrorMessage;


                    });
                    return Object.keys(rowErrors).length > 0 ? rowErrors : null;
                });

                errors[field.name] = tableErrors.some(rowError => rowError !== null) ? tableErrors : null;
            }
        });

        // ----------------------------------------------------------------

        setFormErrors(errors);
        return Object.values(errors).some(error => error !== null) === false;
    }, [dynamicForm, formValues]);


    useEffect(() => {
        if (dynamicForm.length > 0 && initialValidation) {
            validateForm();
        }
    }, [formValues, dynamicForm, initialValidation, validateForm]);


    useEffect(() => {
        if (dynamicFormStructureId && !fields) {
            getDynamicFormConfig(dynamicFormStructureId)
                .then(data => setDynamicForm(data.config || []))
                .catch(error => window.alert(error.message));
        }
        if (fields) {
            setDynamicForm(fields);
        }
    }, [dynamicFormStructureId, fields]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange(formValues);
        }
    }, [formValues, onDataChange]);


    // Quando cambio categoria i dati rimangono salvati, vanno rimossi
    useEffect(() => {
        if (categoria) {
            setFormValues({})
        }
    }, [categoria]);

    const getNumRows = () => {
        return [...new Set(dynamicForm.map(field => field.row ?? 0))].sort((a, b) => a - b);
    };

    const getByRow = row => dynamicForm.filter(f => (f.row ?? 0) === row);

    const handleChange = (e, fieldName, fieldType, fieldRequired) => {
        let {value} = e.target;

        if (fieldType === 'checkbox-text')
            value = e.target.checked;


        // Aggiorna lo stato dei valori del modulo
        setFormValues(prev => ({...prev, [fieldName]: value}));

        let errorMessage = "";

        // Controllo se il campo è obbligatorio
        if (fieldRequired && !value) {
            errorMessage = "Questo campo è obbligatorio";
        }
        // Validazione basata sul tipo di campo
        else if (fieldType && fieldConfig[fieldType]) {
            const {pattern, errorMessage: typeError} = fieldConfig[fieldType];
            if (!new RegExp(pattern).test(value)) {
                errorMessage = typeError;
            }
        }

        // Aggiorna lo stato degli errori solo se necessario
        setFormErrors(prev => ({
            ...prev,
            [fieldName]: errorMessage || null,
        }));
    };

    const handleTableChange = (rowIndex, columnKey, value, fieldName, fieldRequired) => {
        setFormValues(prev => {
            const updatedRows = [...(prev[fieldName] || [])];

            // Aggiorna il valore della cella specifica
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                [columnKey]: value
            };

            return {...prev, [fieldName]: updatedRows};
        });

        let errorMessage = "";

        // Controllo se il campo della cella è obbligatorio
        if (fieldRequired && !value) {
            errorMessage = "Questo campo è obbligatorio";
        }

        // Aggiorna gli errori della tabella
        setFormErrors(prev => ({
            ...prev,
            [`${fieldName}-${rowIndex}-${columnKey}`]: errorMessage || null,
        }));
    };

// Funzione per aggiungere una riga alla tabella
    const handleAddRow = (fieldName, columns = []) => {
        setFormValues(prev => {
            const newRow = columns.reduce((acc, col) => ({...acc, [col.value]: ''}), {});
            return {...prev, [fieldName]: [...(prev[fieldName] || []), newRow]};
        });
    };

// Funzione per rimuovere una riga dalla tabella
    const handleRemoveRow = (fieldName, rowIndex) => {
        setFormValues(prev => {
            const updatedRows = prev[fieldName]?.filter((_, i) => i !== rowIndex) || [];
            return {...prev, [fieldName]: updatedRows};
        });
    };


    const handleDateChange = (e, fieldName) => {
        let value = e.target.value.replace(/\D/g, ''); // Rimuove caratteri non numerici
        if (value.length > 8) value = value.slice(0, 8); // Limita a 8 cifre

        let formattedValue = value;
        if (value.length >= 2) formattedValue = `${value.slice(0, 2)}/${value.slice(2)}`;
        if (value.length >= 4) formattedValue = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;

        setFormValues(prev => ({...prev, [fieldName]: formattedValue}));

        // Validazione del formato
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        setFormErrors(prev => ({
            ...prev,
            [fieldName]: dateRegex.test(formattedValue) ? "" : "Formato non valido (DD/MM/YYYY)"
        }));
    };


    const isSelected = (value, field) => (formValues[field.name] || []).includes(value);

    const countByRow = (rowNumber) => {
        return 12 / dynamicForm.filter(f => f.row === rowNumber).length;
    };

    const dependencyTreeDynamicSystem = (field) => {
        if (field.dependency) {
            console.log(formValues[field.dependency])
            return formValues[field.dependency] === field.dependencyValue;
        }
        return true;
    }

    return (
        <React.Fragment>
            {getNumRows().map(row => (
                <Grid container spacing={1} key={row}>
                    {getByRow(row).map(field => {
                        if ((!(field.mostraPerCensito ?? true) && clienteCensito)) return null;
                        if (!dependencyTreeDynamicSystem(field)) return null;

                        switch (field.type) {
                            case 'text-h1':
                                return (
                                    <Grid item key={field.name} className={classes.fieldItem}>
                                        <Typography
                                            className={classes.largerBold}
                                            style={{
                                                textAlign: 'center',
                                                marginBottom: 16,
                                                width: '100%'
                                            }}
                                        >
                                            {field.label}
                                        </Typography>
                                    </Grid>
                                );
                            case 'text-simple':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        <Typography style={{fontWeight: 'bold', marginTop: 10, marginBottom: 10}}>
                                            {field.label}
                                        </Typography>
                                    </Grid>
                                );
                            case 'text-simple-centered':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        <Typography align="center"
                                                    style={{fontWeight: 'bold', marginTop: 10, marginBottom: 10}}>
                                            {field.label}
                                        </Typography>
                                    </Grid>
                                );
                            case 'text-html':
                                return (
                                    <div key={field.name} className={classes.fieldItem}>
                                        {field.label && (
                                            <Typography
                                                style={{fontWeight: 'bold', marginBottom: 8}}>
                                                {field.label}
                                            </Typography>
                                        )}
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: field.labelAlt || ''
                                            }}
                                        />
                                    </div>
                                );
                            case 'note':
                                return (
                                    <div key={field.name} className={classes.fieldItem}>
                                        {field.label && (
                                            <Typography style={{fontWeight: 'bold'}}>
                                                {field.label}
                                            </Typography>
                                        )}
                                        <Typography>{field.labelAlt}</Typography>
                                    </div>
                                );
                            case 'yellow-note':
                                return (
                                    <div key={field.name} className={classes.fieldItem}>
                                        {field.label && (
                                            <div className={classes.glowYellow}>
                                                <Typography style={{fontWeight: 'bold'}}>
                                                    {field.label}
                                                </Typography>
                                            </div>
                                        )}
                                        <div className={classes.glowYellow}>{field.labelAlt}</div>
                                    </div>
                                );
                            case 'text':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        {field.labelAlt ?
                                            <Tooltip classes={{tooltip: classes.tooltip}} title={field.labelAlt}>
                                                <TextField
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    label={field.label}
                                                    value={formValues[field.name] ?? ''}
                                                    required={(field.required ? (field.required === "true") : false)}
                                                    error={!!formErrors[field.name]} // Evidenzia errore se presente
                                                    helperText={formErrors[field.name] ? formErrors[field.name] : field.labelAlt} // Testo di aiuto
                                                    onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                                />
                                            </Tooltip> : <TextField
                                                fullWidth
                                                name={field.name}
                                                id={field.name}
                                                margin="dense"
                                                variant="outlined"
                                                label={field.label}
                                                value={formValues[field.name] ?? ''}
                                                required={(field.required ? (field.required === "true") : false)}
                                                error={!!formErrors[field.name]} // Evidenzia errore se presente
                                                helperText={formErrors[field.name] || null} // Testo di aiuto
                                                onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                            />}
                                    </Grid>
                                );
                            case 'fiscal_code':
                            case 'cellular':
                            case 'email':
                            case 'money':
                            case 'vat_number':
                                const config = fieldConfig[field.type] || {};
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        <TextField
                                            fullWidth
                                            margin="dense"
                                            variant="outlined"
                                            label={field.label}
                                            placeholder={config.placeholder || ""}
                                            value={formValues[field.name] ?? ''}
                                            required={(field.required ? (field.required === "true") : false)}
                                            onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                            error={!!formErrors[field.name]}
                                            helperText={formErrors[field.name] || config.helperText || ""}
                                            type={config.type || "text"}
                                            inputProps={{
                                                maxLength: config.maxLength,
                                                pattern: config.pattern
                                            }}
                                        />
                                    </Grid>
                                );
                            case 'date':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        {field.labelAlt ?
                                            <Tooltip classes={{tooltip: classes.tooltip}} title={field.labelAlt}>
                                                <TextField
                                                    fullWidth
                                                    margin="dense"
                                                    variant="outlined"
                                                    label={field.label}
                                                    placeholder={"01/01/2025"}
                                                    required={(field.required ? (field.required === "true") : false)}
                                                    value={formValues[field.name] ?? ''}
                                                    onChange={e => handleDateChange(e, field.name)}
                                                    error={!!formErrors[field.name]} // Evidenzia errore se presente
                                                    helperText={formErrors[field.name] ? formErrors[field.name] : field.labelAlt} // Testo di aiuto
                                                    inputProps={{maxLength: 10}} // Limita la lunghezza massima
                                                />
                                            </Tooltip> : <TextField
                                                fullWidth
                                                margin="dense"
                                                variant="outlined"
                                                label={field.label}
                                                placeholder={"01/01/2025"}
                                                required={(field.required ? (field.required === "true") : false)}
                                                value={formValues[field.name] ?? ''}
                                                onChange={e => handleDateChange(e, field.name)}
                                                error={!!formErrors[field.name]} // Evidenzia errore se presente
                                                helperText={formErrors[field.name] || null} // Testo di aiuto
                                                inputProps={{maxLength: 10}} // Limita la lunghezza massima
                                            />}
                                    </Grid>
                                );
                            case 'checkbox':
                                // In Angular usavi radio "Sì/No"
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}
                                          style={{padding: 10}}>
                                        <FormControl component="fieldset" margin="dense">
                                            <FormLabel component="legend">{field.label}</FormLabel>
                                            <RadioGroup
                                                row
                                                value={formValues[field.name] || ''}
                                                onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                            >
                                                <FormControlLabel
                                                    value="si"
                                                    control={<Radio color={"primary"}/>}
                                                    label="Sì"
                                                />
                                                <FormControlLabel
                                                    value="no"
                                                    control={<Radio color={"primary"}/>}
                                                    label="No"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                );
                            case 'checkbox-value':
                                // In Angular usavi radio "Sì/No"
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}
                                          style={{padding: 10}}>
                                        {field.options && field.options.length === 2 && (
                                            <FormControl component="fieldset" margin="dense">
                                                <FormLabel component="legend">{field.label}</FormLabel>
                                                <RadioGroup
                                                    row
                                                    value={formValues[field.name] || ''}
                                                    onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                                >
                                                    <FormControlLabel
                                                        value={field.options[0].value}
                                                        control={<Radio color={"primary"}/>}
                                                        label={field.options[0].value}
                                                    />
                                                    <FormControlLabel
                                                        value={field.options[1].value}
                                                        control={<Radio color={"primary"}/>}
                                                        label={field.options[1].value}
                                                    />
                                                </RadioGroup>
                                            </FormControl>)}
                                        {(!field.options || field.options.length !== 2) && (
                                            <Typography style={{
                                                margin: 15,
                                                border: '2px solid red',
                                                padding: 10,
                                                textAlign: 'center'
                                            }}>
                                                Errore di configurazione, inserire almeno due valori per Opzione
                                                Selezionabile (valore/valore): {field.options.length}
                                            </Typography>
                                        )}
                                    </Grid>
                                )
                                    ;
                            case 'checkbox-text':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}
                                          style={{marginTop: 10, marginBottom: 10}}>
                                        <FormControl component="fieldset" margin="dense" required={field.required}
                                                     style={{display: "flex", alignItems: "flex-start"}}>
                                            <Grid container alignItems="flex-start">
                                                {/* Checkbox in alto a sinistra */}
                                                <Grid item>
                                                    <Checkbox
                                                        style={{marginLeft: -12}}
                                                        checked={Boolean(formValues[field.name])}
                                                        onChange={(e) => handleChange(e, field.name, field.type, field.required)}
                                                        color="primary"
                                                    />
                                                </Grid>
                                                {/* Testo di fianco alla checkbox */}
                                                <Grid item xs style={{paddingLeft: 10, paddingRight: 15}}>
                                                    <Typography variant="body1"
                                                                style={{textAlign: 'justify'}}>{field.labelAlt}</Typography>
                                                </Grid>
                                            </Grid>
                                            {field.required && !formValues[field.name] && (
                                                <FormHelperText error>Questo campo è obbligatorio</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                );


                            case 'select':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        <FormControl variant="outlined" fullWidth margin="dense">
                                            <InputLabel>{field.label}</InputLabel>
                                            <Select
                                                margin='dense'
                                                value={formValues[field.name] || ''}
                                                onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                                label={field.label} // Associa l'etichetta alla Select
                                                MenuProps={MenuProps} // <--- Usa il tuo MenuProps personalizzato
                                            >
                                                {field.options &&
                                                    field.options.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                );
                            case 'multiselect':
                                return (
                                    <Grid item xs={12} sm={countByRow(field.row)} key={field.name}>
                                        <FormControl variant="outlined" fullWidth margin='dense'>
                                            <InputLabel>{field.label}</InputLabel>
                                            <Select
                                                multiple
                                                margin='dense'
                                                value={formValues[field.name] || []}
                                                onChange={e => handleChange(e, field.name, field.type, (field.required ? (field.required === "true") : false))}
                                                label={field.label} // Associa l'etichetta alla Select
                                                renderValue={(selected) =>
                                                    field.options
                                                        .filter((opt) => selected.includes(opt.value))
                                                        .map((opt) => opt.label)
                                                        .join(', ')
                                                }
                                                MenuProps={MenuProps} // <--- Usa il tuo MenuProps personalizzato
                                            >
                                                {field.options &&
                                                    field.options.map((option) => (
                                                        <MenuItem key={option.value}
                                                                  value={option.value}>
                                                            <Checkbox color={"primary"}
                                                                      checked={isSelected(option.value, field)}/>
                                                            <ListItemText primary={option.label}/>
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                );
                            case 'table':
                                return (
                                    <Grid item xs={12} key={field.name} style={{
                                        marginTop: 30,
                                        marginBottom: 30,
                                        display: "flex",
                                        flexDirection: "column"
                                    }}>
                                        <TableContainer component={Paper} className={classesBase.roundBorder}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        {field.options.map(option => (
                                                            <TableCell key={option.value}>{option.label}</TableCell>
                                                        ))}
                                                        <TableCell>Azioni</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {formValues[field.name]?.map((row, rowIndex) => (
                                                        <TableRow key={rowIndex} style={{
                                                            height: "85px",
                                                            verticalAlign: "middle"
                                                        }}> {/* Altezza uniforme */}
                                                            {field.options.map(option => (
                                                                <TableCell key={option.value} style={{
                                                                    verticalAlign: "top",
                                                                    paddingTop: "10px"
                                                                }}>
                                                                    <TextField
                                                                        value={row[option.value] || ''}
                                                                        onChange={e =>
                                                                            handleTableChange(rowIndex, option.value, e.target.value, field.name, field.required)
                                                                        }
                                                                        variant="outlined"
                                                                        fullWidth
                                                                        size="small"
                                                                        error={Boolean(formErrors[`${field.name}-${rowIndex}-${option.value}`])}
                                                                        helperText={formErrors[`${field.name}-${rowIndex}-${option.value}`] || ''}
                                                                        InputProps={{
                                                                            style: {
                                                                                height: "40px",
                                                                                display: "flex",
                                                                                alignItems: "center"
                                                                            }
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            ))}
                                                            <TableCell
                                                                style={{verticalAlign: "middle", textAlign: "center"}}>
                                                                <IconButton onClick={() => handleRemoveRow(field.name, rowIndex)} size="large">
                                                                    <DeleteTwoToneIcon/>
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>

                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Button
                                            variant={"contained"} disableElevation size="small" color="primary"
                                            style={{borderRadius: 24, marginTop: 15, alignSelf: 'center'}}
                                            className={classes.roundButton}
                                            onClick={() => handleAddRow(field.name, field.options || [])}
                                        >
                                            Aggiungi Riga
                                        </Button>
                                    </Grid>
                                );

                            default:
                                return null;
                        }
                    })}
                </Grid>
            ))}
        </React.Fragment>
    );
});

DynamicFormExt.propTypes = {
    dynamicFormStructureId: PropTypes.number,
    clienteCensito: PropTypes.bool.isRequired,
    onDataChange: PropTypes.func
};

export default DynamicFormExt;
